import * as React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import ModalLayout from '@/modules/drivers/modals/shared/ModalLayout'
import { Dropdown } from '@/app/features/company-selection/Dropdown'
import { CompanyIcon } from '@/app/features/icons/Company'
import { useQueryCompanies } from '@/modules/shared/query-client'
import { isUnusableServiceCompany } from '@/app/constants/companyType'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

type Props = {
  companyId: string | null
  onCompanyChange: () => void
}

/**
 * A service company (konto usługowe) has no tachograph database, so every operational
 * screen under it answers 500. The sidebar and the picker no longer lead here, but a
 * bookmark or a pasted URL still can — this explains it instead of leaving a raw error.
 */
export const CompanyHasNoDatabaseModal = ({ companyId, onCompanyChange }: Props) => {
  const { t } = useLocalize()
  const location = useLocation()
  const navigate = useNavigate()
  const companiesQuery = useQueryCompanies({ includeDeleted: false })

  const currentCompany = React.useMemo(() => {
    if (!companyId || !companiesQuery.data) return null
    return companiesQuery.data.find((c) => c.id === companyId) ?? null
  }, [companyId, companiesQuery.data])

  const companiesOptions = (companiesQuery.data ?? [])
    .filter(
      (c) =>
        c.id !== companyId && c.isValid !== false && !isUnusableServiceCompany(c),
    )
    .map((company) => ({
      label: company.companyName || '',
      value: company.id,
      icon: CompanyIcon,
    }))

  const goToCompany = (newCompanyId: string) => {
    onCompanyChange()
    navigate(
      location.pathname.replace(/\/company\/([^/]+)/, `/company/${newCompanyId}`),
    )
  }

  return (
    <ModalLayout>
      <div className="tw-relative tw-flex tw-w-full tw-max-w-[460px] tw-flex-col tw-rounded-lg tw-bg-base-100 tw-p-6 tw-shadow-xl">
        <h2 className="tw-mb-2 tw-text-lg tw-font-semibold tw-text-content-base">
          {t('serviceAccount.no-database-title')}
        </h2>
        <p className="tw-text-sm tw-text-content-secondary">
          {currentCompany?.companyName ? (
            <>
              <span className="tw-font-medium tw-text-content-base">
                {currentCompany.companyName}
              </span>{' '}
              {t('serviceAccount.no-database-description')}
            </>
          ) : (
            t('serviceAccount.no-database-description-generic')
          )}
        </p>

        {companyId && (
          <button
            type="button"
            onClick={() => {
              onCompanyChange()
              navigate(`/et/company/${companyId}/serviceAccount`)
            }}
            className="tw-mt-4 tw-cursor-pointer tw-self-start tw-rounded tw-bg-blue-600 tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-white hover:tw-bg-blue-700"
          >
            {t('serviceAccount.no-database-open-panel')}
          </button>
        )}

        {companiesOptions.length > 0 && (
          <div className="tw-mt-6 tw-border-t tw-border-base-300 tw-pt-4">
            <p className="tw-mb-2 tw-text-xs tw-text-content-subtle">
              {t('serviceAccount.no-database-switch')}
            </p>
            <Dropdown
              icon={CompanyIcon}
              placeholder={t('shared.sidebar.select-company')}
              options={companiesOptions}
              name="company"
              value={null}
              onChange={goToCompany}
            />
          </div>
        )}
      </div>
    </ModalLayout>
  )
}
